const GOOGLE_CALENDAR_URL = "https://calendar.google.com/calendar/render";
const OUTLOOK_CALENDAR_URL = "https://outlook.office.com/calendar/0/deeplink/compose";

function coerceText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function parseTimestamp(value, label) {
  if (!value) {
    throw new Error(`${label} is required.`);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${label} must be a valid timestamp.`);
  }
  return date;
}

export function validateCalendarEvent({
  title,
  startUtc,
  endUtc,
  timezone,
  location,
  description,
  publicEventUrl,
} = {}) {
  const start = parseTimestamp(startUtc, "Calendar event start");
  const end = parseTimestamp(endUtc, "Calendar event end");

  if (end.getTime() <= start.getTime()) {
    throw new Error("Calendar event end must be later than its start.");
  }

  return {
    title: coerceText(title) || "Road Home Program Event",
    start,
    end,
    timezone: coerceText(timezone) || "UTC",
    location: coerceText(location),
    description: coerceText(description),
    publicEventUrl: coerceText(publicEventUrl).replace(/[\r\n]+/g, ""),
  };
}

export function formatUtcTimestamp(value) {
  const date = parseTimestamp(value, "Calendar timestamp");
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

export function escapeIcsText(value) {
  return coerceText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

export function createSafeIcsFilename(title) {
  const safeTitle = coerceText(title)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72)
    .replace(/-+$/g, "");

  return `${safeTitle || "road-home-event"}.ics`;
}

export function findCalendarSlotById(slots, selectedSlotId) {
  if (!Array.isArray(slots) || selectedSlotId === undefined || selectedSlotId === null) {
    return null;
  }

  const normalizedId = String(selectedSlotId).trim();
  if (!normalizedId) return null;

  return slots.find((slot) => slot && String(slot.id) === normalizedId) || null;
}

function buildDescription(event) {
  return [
    event.description,
    event.publicEventUrl ? `Event page: ${event.publicEventUrl}` : "",
  ].filter(Boolean).join("\n");
}

export function createGoogleCalendarUrl(calendarEvent) {
  const event = validateCalendarEvent(calendarEvent);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatUtcTimestamp(event.start)}/${formatUtcTimestamp(event.end)}`,
    details: buildDescription(event),
    location: event.location,
    ctz: event.timezone,
  });

  return `${GOOGLE_CALENDAR_URL}?${params.toString()}`;
}

export function createOutlookCalendarUrl(calendarEvent) {
  const event = validateCalendarEvent(calendarEvent);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: event.start.toISOString(),
    enddt: event.end.toISOString(),
    body: buildDescription(event),
    location: event.location,
  });

  return `${OUTLOOK_CALENDAR_URL}?${params.toString()}`;
}

function utf8ByteLength(character) {
  const codePoint = character.codePointAt(0);
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}

function foldIcsLine(line) {
  const folded = [];
  let current = "";
  let currentBytes = 0;

  for (const character of line) {
    const characterBytes = utf8ByteLength(character);
    if (current && currentBytes + characterBytes > 75) {
      folded.push(current);
      current = ` ${character}`;
      currentBytes = 1 + characterBytes;
    } else {
      current += character;
      currentBytes += characterBytes;
    }
  }

  folded.push(current);
  return folded.join("\r\n");
}

function createCalendarUid(event) {
  const source = `${event.title}|${event.start.toISOString()}|${event.publicEventUrl}`;
  let hash = 2166136261;

  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `rhp-${(hash >>> 0).toString(36)}-${formatUtcTimestamp(event.start)}@roadhome.io`;
}

export function createIcsContent(calendarEvent, { now = new Date() } = {}) {
  const event = validateCalendarEvent(calendarEvent);
  const description = buildDescription(event);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Road Home Program//OutreachOS Event Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-TIMEZONE:${escapeIcsText(event.timezone)}`,
    "BEGIN:VEVENT",
    `UID:${createCalendarUid(event)}`,
    `DTSTAMP:${formatUtcTimestamp(now)}`,
    `DTSTART:${formatUtcTimestamp(event.start)}`,
    `DTEND:${formatUtcTimestamp(event.end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
  ];

  if (event.location) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  if (event.publicEventUrl) lines.push(`URL:${event.publicEventUrl}`);

  lines.push("END:VEVENT", "END:VCALENDAR");
  return `${lines.map(foldIcsLine).join("\r\n")}\r\n`;
}

export function downloadIcsFile(calendarEvent) {
  const event = validateCalendarEvent(calendarEvent);
  if (typeof document === "undefined" || typeof URL === "undefined") {
    throw new Error("Calendar downloads require a browser.");
  }

  const objectUrl = URL.createObjectURL(
    new Blob([createIcsContent(calendarEvent)], { type: "text/calendar;charset=utf-8" })
  );
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = createSafeIcsFilename(event.title);
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
